import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import {
  deleteContact,
  getContacts,
  updateContact,
} from "../services/contactService";
import {
  setResults,
  setSearchTerm,
  setSelectedContact,
  setSelectedContacts,
  setTempData,
} from "../features/contactSlice";
import { setContact, setContacts } from "../features/assessorSlice";
import { getDefinitions } from "../services/settingsService";
import { setDefinitions } from "../features/settingsSlice";
import CreateContact from "./createContact";
import ContactEnrichmentDialog from "./contactEnrichmentDialog";
import CustomFieldInput from "./customFieldInput";

const contactDefaults = {
  name: "",
  phone_number: "",
  email: "",
  company: "",
  secondary_contact: "",
  secondary_phone: "",
  secondary_email: "",
  notes: "",
  callsMade: 0,
  followUpCalls: 0,
  emailsSent: 0,
  dropIns: 0,
  custom_fields: {},
};

const fieldRows = [
  { name: "name", label: "Name" },
  { name: "phone_number", label: "Phone Number" },
  { name: "email", label: "Email" },
  { name: "company", label: "Company" },
  { name: "secondary_contact", label: "Secondary Contact" },
  { name: "secondary_phone", label: "Secondary Phone" },
  { name: "secondary_email", label: "Secondary Email" },
];

const metricRows = [
  { name: "callsMade", label: "Calls Made" },
  { name: "followUpCalls", label: "Follow-Up Calls" },
  { name: "emailsSent", label: "Emails Sent" },
  { name: "dropIns", label: "Drop-Ins" },
];

const normalizeContact = (contact) => ({
  ...contactDefaults,
  ...contact,
  callsMade: contact?.callsMade ?? 0,
  followUpCalls: contact?.followUpCalls ?? 0,
  emailsSent: contact?.emailsSent ?? 0,
  dropIns: contact?.dropIns ?? 0,
  custom_fields: contact?.custom_fields || {},
});

const Contacts = ({ mode = "page" }) => {
  const isEmbedded = mode === "embedded";
  const contacts = useSelector((state) => state.contact.value);
  const settingsLoaded = useSelector((state) => state.settings.value.loaded);
  const contactFields = useSelector((state) => state.settings.value.contactFields);
  const selectedContactIds = useMemo(
    () => contacts.selectedContacts || [],
    [contacts.selectedContacts]
  );
  const selectedContactData = useMemo(() => {
    if (!contacts.selectedContact) {
      return null;
    }

    const fromResults = contacts.results?.find((c) => c.id === contacts.selectedContact) || null;
    if (fromResults) {
      return fromResults;
    }

    if (contacts.tempData?.id === contacts.selectedContact) {
      return contacts.tempData;
    }

    return null;
  }, [contacts.results, contacts.selectedContact, contacts.tempData]);

  const dispatch = useDispatch();

  const [createOpen, setCreateOpen] = useState(false);
  const [enrichmentOpen, setEnrichmentOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: contactDefaults,
  });

  useEffect(() => {
    if (!settingsLoaded) {
      getDefinitions().then((defs) => {
        if (defs) {
          dispatch(setDefinitions(defs));
        }
      });
    }
  }, [settingsLoaded, dispatch]);

  useEffect(() => {
    if (selectedContactData) {
      const normalized = normalizeContact(selectedContactData);
      reset(normalized);
      if (isEmbedded) {
        dispatch(setContact(normalized.id));
      }
      return;
    }

    reset(contactDefaults);
    if (isEmbedded) {
      dispatch(setContact(selectedContactIds.length ? selectedContactIds[0] : null));
    }
  }, [selectedContactData, dispatch, isEmbedded, reset, selectedContactIds]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await getContacts(contacts.searchTerm || "");
      if (cancelled) {
        return;
      }

      const results = res || [];
      dispatch(setResults(results));
      setLoading(false);
    }, isEmbedded ? 120 : 220);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [contacts.searchTerm, contacts.selectedContact, dispatch, isEmbedded]);

  const selectContact = (contact) => {
    dispatch(setSelectedContact(contact.id));
    dispatch(setTempData(contact));
    if (isEmbedded) {
      dispatch(setContact(contact.id));
    }
  };

  const toggleContactSelection = (contact) => {
    const exists = selectedContactIds.includes(contact.id);
    const nextSelected = exists
      ? selectedContactIds.filter((id) => id !== contact.id)
      : [...selectedContactIds, contact.id];

    dispatch(setSelectedContacts(nextSelected));
    if (isEmbedded) {
      dispatch(setContacts(nextSelected));
      dispatch(setContact(nextSelected.length ? nextSelected[0] : null));
    }

    if (!contacts.selectedContact) {
      dispatch(setSelectedContact(contact.id));
      dispatch(setTempData(contact));
    }
  };

  const clearSelection = () => {
    dispatch(setSelectedContact(null));
    dispatch(setSelectedContacts([]));
    dispatch(setTempData(null));
    if (isEmbedded) {
      dispatch(setContacts([]));
      dispatch(setContact(null));
    }
    reset(contactDefaults);
  };

  const refreshResults = async (query) => {
    const result = await getContacts(query ?? contacts.searchTerm ?? "");
    const rows = result || [];
    dispatch(setResults(rows));
    return rows;
  };

  const onUpdate = async (data) => {
    if (!contacts.selectedContact) {
      return;
    }

    setSaving(true);
    try {
      await updateContact({
        ...data,
        id: contacts.selectedContact,
      });
      const rows = await refreshResults(data.name || contacts.searchTerm);
      const updated = rows.find((c) => c.id === contacts.selectedContact);
      if (updated) {
        selectContact(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!contacts.selectedContact) {
      return;
    }

    setDeleting(true);
    try {
      await deleteContact(contacts.selectedContact);
      await refreshResults(contacts.searchTerm);
      clearSelection();
    } finally {
      setDeleting(false);
    }
  };

  const handleEnrichmentApplied = async (updatedContact) => {
    if (!updatedContact?.name) {
      return;
    }

    dispatch(setSearchTerm(updatedContact.name));
    const rows = await refreshResults(updatedContact.name);
    const matched = rows.find((c) => c.id === updatedContact.id);
    if (matched) {
      selectContact(matched);
    }
  };

  const handleContactCreated = async (createdInput) => {
    const query = createdInput?.name || contacts.searchTerm || "";
    dispatch(setSearchTerm(query));
    const rows = await refreshResults(query);
    if (!rows.length) {
      return;
    }

    const exactMatch = rows.find(
      (row) =>
        String(row?.name || "").trim().toLowerCase() ===
        String(createdInput?.name || "").trim().toLowerCase()
    );
    const next = exactMatch || rows[0];
    selectContact(next);
  };

  const listContent = (
    <Card variant="outlined" sx={{ height: isEmbedded ? 300 : 560, overflow: "hidden" }}>
      <CardContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            label="Search Contacts"
            value={contacts.searchTerm}
            onChange={(e) => dispatch(setSearchTerm(e.target.value))}
            fullWidth
            size={isEmbedded ? "small" : "medium"}
            placeholder="Name, company, phone, email"
          />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : contacts.results?.length ? (
            <List disablePadding>
              {contacts.results.map((contact) => (
                <ListItemButton
                  key={contact.id}
                  selected={contact.id === contacts.selectedContact}
                  onClick={() => selectContact(contact)}
                  divider
                >
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={selectedContactIds.includes(contact.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleContactSelection(contact)}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primary={contact.name || "Unnamed Contact"}
                    secondary={`${contact.company || "No company"}${
                      contact.phone_number ? ` • ${contact.phone_number}` : ""
                    }`}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No contacts found. Adjust your query or create a new contact.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (isEmbedded) {
    return (
      <Grid container spacing={1.5}>
        <CreateContact
          dialogOpen={createOpen}
          handleClose={setCreateOpen}
          onCreated={handleContactCreated}
        />
        <ContactEnrichmentDialog
          open={enrichmentOpen}
          onClose={setEnrichmentOpen}
          onApplied={handleEnrichmentApplied}
        />

        <Grid item xs={12}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={() => setCreateOpen(true)}>
              Create
            </Button>
            <Button variant="contained" onClick={() => setEnrichmentOpen(true)}>
              Enrich
            </Button>
            <Button variant="outlined" color="inherit" onClick={clearSelection}>
              Clear
            </Button>
            {selectedContactIds.length > 0 && (
              <Chip
                size="small"
                label={`Selected ${selectedContactIds.length}`}
                color="primary"
              />
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {listContent}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ p: { xs: 1, md: 2 } }}>
      <CreateContact
        dialogOpen={createOpen}
        handleClose={setCreateOpen}
        onCreated={handleContactCreated}
      />
      <ContactEnrichmentDialog
        open={enrichmentOpen}
        onClose={setEnrichmentOpen}
        onApplied={handleEnrichmentApplied}
      />

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h5">Contact Workspace</Typography>
                <Typography variant="body2" color="text.secondary">
                  Search, review, and update contacts from a single screen.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="outlined" onClick={() => setCreateOpen(true)}>
                  Create Contact
                </Button>
                <Button variant="contained" onClick={() => setEnrichmentOpen(true)}>
                  Enrich Existing Contact
                </Button>
                <Button variant="outlined" color="inherit" onClick={clearSelection}>
                  Clear Selection
                </Button>
                {selectedContactIds.length > 0 ? (
                  <Chip label={`Checked ${selectedContactIds.length}`} color="primary" />
                ) : null}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        {listContent}
      </Grid>

      <Grid item xs={12} md={8}>
        <Card variant="outlined" sx={{ minHeight: 560 }}>
          <CardContent>
            {selectedContactData ? (
              <form onSubmit={handleSubmit(onUpdate)}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {selectedContactData.name || "Selected Contact"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID #{selectedContactData.id}
                    </Typography>
                  </Grid>
                    {fieldRows.map((row) => (
                      <Grid item xs={12} md={6} key={row.name}>
                        <Controller
                          name={row.name}
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} fullWidth label={row.label} />
                          )}
                        />
                      </Grid>
                    ))}
                    {contactFields
                      .filter((f) => !f.archived)
                      .map((def) => (
                        <Grid item xs={12} md={6} key={`cf-${def.id}`}>
                          <Controller
                            name={`custom_fields.${def.internal_name}`}
                            control={control}
                            render={({ field }) => (
                              <CustomFieldInput
                                def={def}
                                value={field.value}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </Grid>
                      ))}

                    <Grid item xs={12}>
                      <Controller
                        name="notes"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth multiline minRows={5} label="Notes" />
                        )}
                      />
                    </Grid>

                    {metricRows.map((row) => (
                      <Grid item xs={6} md={3} key={row.name}>
                        <Controller
                          name={row.name}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="number"
                              label={row.label}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    ))}
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={!isDirty || saving}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        disabled={deleting}
                        onClick={onDelete}
                      >
                        {deleting ? "Deleting..." : "Delete Contact"}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            ) : (
              <Stack spacing={1}>
                <Typography variant="h6">No Contact Selected</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a contact from the list to edit details, track activity, or remove it.
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Contacts;
